import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { ToastProvider } from './components/Toast';
import { Login } from './routes/Login';
import { ChangePassword } from './routes/ChangePassword';

/*
 * Una sola aplicación con dos árboles de rutas (decisión 1A).
 * Cada árbol se carga de forma diferida: un cliente no descarga el panel
 * administrativo. No es una frontera de seguridad —esa vive en el API— sino
 * una forma de no enviar código que no se va a usar.
 */
const AdminLayout = lazy(() => import('./routes/admin/AdminLayout'));
const PortalLayout = lazy(() => import('./routes/portal/PortalLayout'));

function Loading({ label = 'Cargando…' }: { label?: string }) {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '60vh', color: 'var(--ink-soft)' }}>
      <p role="status">{label}</p>
    </div>
  );
}

/** Exige sesión, y opcionalmente rol interno, antes de renderizar. */
function Protected({ children, internalOnly = false }: { children: JSX.Element; internalOnly?: boolean }) {
  const { user, status, isInternal } = useAuth();

  if (status === 'loading') return <Loading label="Verificando tu sesión…" />;
  if (status === 'anonymous' || !user) return <Navigate to="/ingresar" replace />;
  // El cambio obligatorio de contraseña bloquea el resto de la aplicación.
  if (user.passwordChangeRequired) return <Navigate to="/cambiar-contrasena" replace />;
  if (internalOnly && !isInternal) return <Navigate to="/portal" replace />;

  return children;
}

/** Envía a cada usuario a su portada según el rol. */
function Home() {
  const { user, status, isInternal } = useAuth();
  if (status === 'loading') return <Loading />;
  if (!user) return <Navigate to="/ingresar" replace />;
  return <Navigate to={isInternal ? '/admin' : '/portal'} replace />;
}

export function App() {
  return (
    <AuthProvider>
      <ToastProvider>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/" element={<Home />} />
          <Route path="/ingresar" element={<Login />} />
          <Route
            path="/cambiar-contrasena"
            element={(
              <ProtectedForPasswordChange>
                <ChangePassword />
              </ProtectedForPasswordChange>
            )}
          />
          <Route
            path="/admin/*"
            element={<Protected internalOnly><AdminLayout /></Protected>}
          />
          <Route path="/portal/*" element={<Protected><PortalLayout /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </ToastProvider>
    </AuthProvider>
  );
}

/**
 * El cambio de contraseña necesita sesión pero no puede exigir que la
 * contraseña ya esté rotada: sería un ciclo infinito de redirecciones.
 */
function ProtectedForPasswordChange({ children }: { children: JSX.Element }) {
  const { user, status } = useAuth();
  if (status === 'loading') return <Loading />;
  if (!user) return <Navigate to="/ingresar" replace />;
  return children;
}
